// A vendor's payout contract (aldoo-na store/payouts/contracts), as the
// Payouts section's Contract tab shows it: the terms in force — the commission,
// what is the vendor's, when sales are paid — and the contract itself in the
// framework's PDF viewer; a contract waiting for the vendor is read and signed
// right there, the sign tools beside its pages (a drawn or typed
// signature + the typed name), which the server stamps into the contract's
// signature block. The admin sets the terms and sends the contract
// (TermsEditor: the vendor's share; with a contract in force, new terms or a
// correction of it, with a reason the vendor sees), sends it again, and sees
// every version with why it was made. On a computer the signature can be
// drawn on the signer's phone too (a QR code of /sign-on-phone, SignOnPhone).
// The admin can send an uploaded PDF instead of the template's text (signed,
// a signature page is appended) and upload contracts signed before; below
// the contract, its history (ContractHistory), for the vendor too.
import React, { useEffect, useMemo, useState } from "react"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import usePdfModal from "./usePdfModal"
import Value from "aldoo-ra/Value"
import { showSuccessToast, showErrorToast } from "aldoo-ra/Toast"
import { Field, NumberInput, Select, Toggle, TextArea, TextInput, Empty, dateOnly, money } from "../AdminShared/ui"
import { PayoutsContractSign, PayoutsAdmin, PayoutsAdminUpload } from "../AdminShared/api"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { PAYOUTS } from "../Vendor/texts/bundles"
import ContractHistory, { PdfPicker, UPLOAD_ERRORS } from "./ContractHistory"
import SignDialog, { accountName, phoneSigning } from "./SignDialog"
import ActionsMenu from "./ActionsMenu"
import ContractPdf from "./ContractPdf"
import ShareContractDialog from "./ShareContractDialog"
import { openChecklist } from "../Vendor/EligibilityChecklist"

const signTexts = (t) => ({
  title: t`Sign the contract`,
  signature: t`Your signature`,
  draw: t`Draw`,
  type: t`Type`,
  color: t`Color`,
  black: t`Black`,
  blue: t`Blue`,
  drawHere: t`Sign here with your mouse or finger`,
  typeHere: t`Type your name`,
  clear: t`Clear`,
  change: t`Change`,
  useSignature: t`Use this signature`,
  name: t`Your full name`,
  nameShort: t`Enter your first and last name, in letters only`,
  sign: t`Sign the contract`,
  signing: t`Signing…`,
  phone: t`Phone`,
  phoneTitle: t`Sign with your phone`,
  phoneHint: t`Scan the code with your phone's camera, sign on its screen, and the signature appears here.`,
  phoneLink: t`Or open this link on your phone:`,
  copyLink: t`Copy link`,
  copied: t`Copied`,
  phoneWaiting: t`Waiting for the signature from your phone…`,
  phoneValidUntil: t`The code is valid until %s`,
  phoneExpired: t`The code has expired`,
  phoneError: t`The code could not be made`,
  newCode: t`New code`
})

/** The sign tools beside the contract waiting for the vendor. */
function SignPanel({ contract, onSigned }) {
  const t = usePortalTexts(PAYOUTS)
  const [open, setOpen] = useState(false)
  const [auth] = Value("user-credentials")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const texts = useMemo(() => signTexts(t), [t])
  const mobile = useMemo(() => phoneSigning("payout_contract", contract._id), [contract._id])

  const sign = async ({ name, image }) => {
    setBusy(true)
    setError(null)
    const res = await PayoutsContractSign({ contractId: contract._id, name, image }).catch((e) => ({ error: e?.message }))
    setBusy(false)
    if (!res || res.error) {
      setError(res?.error === "signer_name_invalid" ? t`Enter your first and last name, in letters only` : t`The contract could not be signed`)
      return showErrorToast(t`The contract could not be signed`, res?.error)
    }
    showSuccessToast(t`Contract signed`)
    setOpen(false)
    onSigned?.(res.contract)
  }

  return (
    <>
      <Button variant="admin_primary" className="h-8 px-4 text-xs" onClick={() => setOpen(true)} data-testid="contract-sign-form">
        {t`Sign`}
      </Button>
      <SignDialog
        open={open}
        onClose={() => setOpen(false)}
        title={t`Sign the contract`}
        onSign={sign}
        busy={busy}
        error={error}
        defaultName={accountName(auth)}
        consentText={t`I have read the contract and I sign it on behalf of the company`}
        texts={texts}
        mobile={mobile}
        data-testid="contract-sign-tools"
      />
    </>
  )
}

// what the server answers a contract it could not issue with, as the admin reads it
const ISSUE_ERRORS = {
  ...UPLOAD_ERRORS,
  platform_signature_missing: "Set Aldoo's signature in Admin → Vendors → Contracts first",
  vendor_percent_invalid: "The vendor's share must be 0–100, a whole number or one decimal",
  hold_days_invalid: "Hold days must be a whole number, 0 or more",
  payout_day_invalid: "The payout day must be 1–28",
  min_payout_invalid: "The minimum payout must be 0 or more",
  reason_required: "Say why the contract changes",
  changes_required: "List what changes in the contract",
  nothing_to_correct: "There is no signed contract to correct",
}

const REASONS = [
  { value: "terms", label: "New terms — from the day the vendor signs" },
  { value: "correction", label: "Correction — an error in the contract in force" },
]

const round1 = (n) => Math.round(n * 10) / 10
const sumOf = (rows, which) => (rows || []).map((c) => `${money(c[which].delta, c.currency)} (${c[which].rows} rows)`).join(", ")

/**
 * The admin's terms: a contract (the vendor's share — Aldoo's commission the
 * rest —, hold days, payout day) generated, signed for Aldoo and sent to the
 * vendor (a draft sent once their payout details are complete). With a
 * contract in force it is either new terms (from its signing; sales before
 * keep the old terms) or a correction of the one in force (from its start:
 * unpaid sales since then settled again, paid ones adjusted on the next
 * payout), with the admin's reason — both shown to the vendor. "Use an
 * uploaded PDF": the PDF is the document the vendor reads and signs (a
 * signature page appended when signed); the terms entered here are still
 * what the sales are settled at.
 */
export function TermsEditor({ orgId, current, inForce, onDone, onCancel }) {
  const [form, setForm] = useState({
    vendorPercent: current ? round1(100 - Number(current.commissionPercent)) : null,
    holdDays: current?.holdDays ?? null,
    payoutDay: current?.payoutDay ?? null,
    minPayout: current?.minPayout ?? null,
    deliveryToVendor: current?.deliveryToVendor !== false,
    notes: "",
    reason: "terms",
    reasonText: "",
    changes: "",
    usePdf: false,
    title: "",
  })
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [impact, setImpact] = useState(null)
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))
  const correction = !!inForce && form.reason === "correction"

  // a first contract starts from the store's terms
  useEffect(() => {
    if (current) return
    PayoutsAdmin("defaults")
      .then((res) => res?.defaults && setForm((f) => ({ ...f, vendorPercent: res.defaults.vendorPercent, holdDays: res.defaults.holdDays, payoutDay: res.defaults.payoutDay, minPayout: res.defaults.minPayout ?? 25 })))
      .catch(() => setForm((f) => ({ ...f, vendorPercent: 80, holdDays: 30, payoutDay: 5, minPayout: 25 })))
  }, [current])

  const share = Number(form.vendorPercent)
  const shareValid = form.vendorPercent !== null && Number.isFinite(share) && share >= 0 && share <= 100 && round1(share) === share
  const daysValid = Number.isInteger(Number(form.holdDays)) && Number(form.holdDays) >= 0
  const dayValid = Number.isInteger(Number(form.payoutDay)) && Number(form.payoutDay) >= 1 && Number(form.payoutDay) <= 28
  const minValid = form.minPayout === null || form.minPayout === "" || (Number(form.minPayout) >= 0 && Math.round(Number(form.minPayout) * 100) === Number(form.minPayout) * 100)
  const reasonValid = !inForce || form.reasonText.trim().length >= 3
  const changesValid = !inForce || form.changes.trim().length >= 3
  const valid = shareValid && daysValid && dayValid && minValid && reasonValid && changesValid && (!form.usePdf || !!file)

  // a correction: what it would change of the sales since the corrected contract's start
  useEffect(() => {
    if (!correction || !shareValid) return setImpact(null)
    let gone = false
    const timer = setTimeout(() => {
      PayoutsAdmin("correction_impact", { orgId, vendorPercent: share, deliveryToVendor: form.deliveryToVendor })
        .then((res) => !gone && setImpact(res?.error ? null : res))
        .catch(() => !gone && setImpact(null))
    }, 300)
    return () => {
      gone = true
      clearTimeout(timer)
    }
  }, [correction, shareValid, share, form.deliveryToVendor, orgId])

  const issue = async () => {
    setBusy(true)
    const { vendorPercent, holdDays, payoutDay, minPayout, deliveryToVendor, notes, reason, reasonText, changes, usePdf, title } = form
    const options = { orgId, vendorPercent, holdDays, payoutDay, minPayout, deliveryToVendor, notes: usePdf ? "" : notes, ...(inForce ? { reason, reasonText: reasonText.trim(), changes: changes.trim() } : {}) }
    const res = await (usePdf ? PayoutsAdminUpload("contract_issue_pdf", file, { ...options, title: title.trim() }) : PayoutsAdmin("contract_issue", options)).catch((e) => ({ error: e?.response?.data?.error || e?.message }))
    setBusy(false)
    if (!res || res.error) return showErrorToast("The contract could not be sent", ISSUE_ERRORS[res?.error] || res?.error)
    showSuccessToast(res.sent ? "Contract sent to the vendor for signature" : "Contract ready — it is sent once the vendor completes their payout details")
    onDone?.()
  }

  return (
    <div className="flex flex-col gap-3 rounded-md border border-admin_bordered p-3" data-testid="contract-terms-editor">
      <div className="text-sm font-semibold">{inForce ? "Change the contract" : "Contract terms"}</div>
      {inForce && (
        <Field label="What is this change">
          <Select value={form.reason} onChange={(v) => v && set("reason", v)} options={REASONS} className="w-full" data-testid="terms-reason" />
        </Field>
      )}
      <div className="grid sm:grid-cols-4 gap-3">
        <Field label="Vendor's share %" error={form.vendorPercent !== null && !shareValid ? "0–100, one decimal at most" : null} hint={shareValid ? `Aldoo commission ${round1(100 - share)}%` : null}>
          <NumberInput value={form.vendorPercent} onChange={(v) => set("vendorPercent", v)} allowEmpty step="0.1" min="0" max="100" data-testid="terms-share" />
        </Field>
        <Field label="Hold days before payout" error={form.holdDays !== null && !daysValid ? "0 or more" : null}>
          <NumberInput value={form.holdDays} onChange={(v) => set("holdDays", v)} min="0" />
        </Field>
        <Field label="Payout day of month (1–28)" error={form.payoutDay !== null && !dayValid ? "1–28" : null}>
          <NumberInput value={form.payoutDay} onChange={(v) => set("payoutDay", v)} min="1" max="28" />
        </Field>
        {/* a smaller balance waits for the next payout (the contract's end pays it) */}
        <Field label="Minimum payout (EUR)" error={!minValid ? "0 or more, cents at most" : null} hint="Smaller balances wait for the next payout">
          <NumberInput value={form.minPayout} onChange={(v) => set("minPayout", v)} allowEmpty step="1" min="0" data-testid="terms-min-payout" />
        </Field>
      </div>
      <Toggle checked={form.deliveryToVendor} onChange={(v) => set("deliveryToVendor", v)} label="Delivery fee goes to the vendor in full (no commission)" />
      {inForce && (
        <Field label="Reason (shown to the vendor)" error={form.reasonText && !reasonValid ? "A few words at least" : null}>
          <TextArea value={form.reasonText} onChange={(v) => set("reasonText", v)} rows={2} placeholder={correction ? "What was wrong in the contract in force" : "Why the terms change"} data-testid="terms-reason-text" />
        </Field>
      )}
      {/* the summary the vendor reads under "Changes" in the contract section, before signing */}
      {inForce && (
        <Field label="Changes (shown to the vendor)" hint="One change per line, in short — the vendor reads them under Changes in their contract section" error={form.changes && !changesValid ? "A few words at least" : null}>
          <TextArea value={form.changes} onChange={(v) => set("changes", v)} rows={5} placeholder={"т. 3.4 — отчет за продажбите към всяко изплащане\nт. 2.6 — комисионата се посочва в отчета"} data-testid="terms-changes" />
        </Field>
      )}
      <Toggle checked={form.usePdf} onChange={(v) => set("usePdf", v)} label="Use an uploaded PDF instead of the template" data-testid="terms-use-pdf" />
      {form.usePdf ? (
        <div className="flex flex-col gap-3 rounded-md bg-admin_neutral_1 p-3" data-testid="terms-pdf">
          <PdfPicker file={file} onChange={setFile} data-testid="terms-pdf-file" />
          <Field label="Title (optional, shown to the vendor)">
            <TextInput value={form.title} onChange={(v) => set("title", v)} placeholder="e.g. Distribution agreement 2027" maxLength={200} />
          </Field>
          <div className="text-xs text-admin_text_secondary">
            The vendor reads and signs this PDF as it is. When signed, a signature page is appended — the parties, the terms above, Aldoo's signature and the vendor's, and the audit trail with
            the document's SHA-256. The terms above are what the vendor's sales are settled at: make sure they match the document.
          </div>
        </div>
      ) : (
        <Field label="Additional terms (optional)">
          <TextArea value={form.notes} onChange={(v) => set("notes", v)} rows={2} />
        </Field>
      )}
      {inForce && (
        <div className="rounded-md bg-admin_neutral_1 px-3 py-2 text-xs text-admin_text_secondary" data-testid="terms-effect">
          {correction ? (
            <>
              Replaces v{inForce.version} from its start ({dateOnly(inForce.effectiveFrom || inForce.signedAt)}). Once signed, unpaid sales since then are settled again at the corrected terms; sales already paid
              keep what was paid and the difference is added to (or taken off) the next payout as an adjustment.
              {impact?.impact && (
                <div className="mt-1 text-admin_text" data-testid="terms-impact">
                  {impact.impact.length
                    ? <>Unpaid sales: {sumOf(impact.impact, "unpaid") || "no change"} · paid sales, adjusted on the next payout: {sumOf(impact.impact, "paid") || "no change"}</>
                    : "No sales since then change."}
                </div>
              )}
            </>
          ) : (
            <>Replaces v{inForce.version} from the day the vendor signs; sales before that keep the current terms.</>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="admin_primary" disabled={busy || !valid} onClick={issue} data-testid="terms-send">
          {busy ? (form.usePdf ? "Uploading…" : "Generating…") : form.usePdf ? "Upload and send for signature" : "Generate and send for signature"}
        </Button>
        <Button variant="admin_secondary" onClick={onCancel}>Cancel</Button>
        <span className="text-xs text-admin_text_secondary">The vendor is emailed; without complete payout details the contract waits and is sent when they are.</span>
      </div>
    </div>
  )
}


/** Why a contract waiting for the vendor replaces the one before: new terms or a correction, with the admin's words. */

export default function ContractCard({ orgId, contract, canSign, admin, onChanged, locale }) {
  // the sample contract: the whole screen on a phone (its line of text above the viewer)
  const samplePdf = usePdfModal({ desktopHeight: "72vh", reserve: 40 })
  const t = usePortalTexts(PAYOUTS)
  const [editing, setEditing] = useState(false)
  // the sample contract open (nothing sent to them yet)
  const [sampleOpen, setSampleOpen] = useState(false)
  // that sample mailed to someone to look at it (an accountant)
  const [sharing, setSharing] = useState(false)
  const inForce = contract?.inForce
  const pending = contract?.pending
  // a contract made before the vendor's payout details were complete: sent when they are
  const waiting = !pending && contract?.draft?.sendWhenReady ? contract.draft : null
  const [resending, setResending] = useState(false)
  // the admin's "Regenerate": the pending contract filled again with the company's details as they are now
  const [regenerating, setRegenerating] = useState(false)
  const regenerate = async () => {
    setRegenerating(true)
    const res = await PayoutsAdmin("contract_regenerate", { orgId }).catch((e) => ({ error: e?.message }))
    setRegenerating(false)
    if (!res || res.error) return showErrorToast("The contract could not be regenerated", res?.error)
    if (!res.regenerated?.length) return showErrorToast("Nothing to regenerate", "No contract of the template's text is waiting for a signature")
    showSuccessToast(`Contract ${res.regenerated.join(", ")} regenerated`)
    onChanged?.()
  }
  const resend = async () => {
    setResending(true)
    const res = await PayoutsAdmin("contract_send", { contractId: pending._id }).catch((e) => ({ error: e?.message }))
    setResending(false)
    if (!res || res.error) return showErrorToast("The contract could not be sent", res?.missing ? `Missing payout details: ${res.missing.join(", ")}` : res?.error)
    showSuccessToast("Contract sent again")
    onChanged?.()
  }

  return (
    <div className="flex flex-col gap-4" data-testid="payout-contract">
      {waiting && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-admin_bordered bg-admin_neutral_1 p-3 text-sm" data-testid="contract-waiting">
          <span className="flex-1 min-w-[220px]">
            {t`Your contract will be sent to you to sign once your payout details are complete.`}
          </span>
          {admin && (
            <Button variant="admin_secondary" onClick={async () => (await PayoutsAdmin("contract_void", { contractId: waiting._id }), onChanged?.())}>
              Withdraw
            </Button>
          )}
        </div>
      )}

      {/* nothing sent yet: what they would sign, with sample data, a click away */}
      {!inForce && !pending && !waiting && (
        <div className="flex flex-col items-center gap-3 py-4 text-center text-sm text-admin_text_secondary" data-testid="contract-none">
          {/* "the required details": the company checklist above (opened and brought into view);
              on the admin's vendor page, which has no checklist, plain words */}
          <span>
            {(() => {
              const [before, after = ""] = t`You will receive your contract here once you fill in all the %s.`.split("%s")
              const words = t`required details`
              return (
                <>
                  {before}
                  {admin ? words : (
                    <button type="button" onClick={openChecklist} className="text-admin_primary underline underline-offset-2 hover:opacity-80" data-testid="contract-required-details">
                      {words}
                    </button>
                  )}
                  {after}
                </>
              )
            })()}
          </span>
          <span>{t`Meanwhile, you can look through the terms of the contract.`}</span>
          <div className="flex flex-wrap justify-center gap-3">
            <Button variant="admin_secondary" onClick={() => setSampleOpen(true)} data-testid="contract-sample-open">
              {t`View the contract`}
            </Button>
            <Button variant="admin_secondary" onClick={() => setSharing(true)} data-testid="contract-share-open">
              {t`Send for review`}
            </Button>
          </div>
        </div>
      )}
      {sharing && <ShareContractDialog orgId={orgId} t={t} onClose={() => setSharing(false)} />}
      <Modal
        isOpen={sampleOpen}
        onClose={() => setSampleOpen(false)}
        title={t`Payout contract (sample)`}
        variant={samplePdf.variant}
        closeOnEscape
        closeOnClickOutside
        styleOverrides={samplePdf.styleOverrides}
      >
        {sampleOpen && (
          <div className="flex flex-col gap-2" data-testid="contract-sample">
            <div className="text-xs text-admin_text_secondary px-3 md:px-0">{t`With sample data: the contract you are sent carries your company's details and terms.`}</div>
            <ContractPdf sampleFor={orgId} downloadable height={samplePdf.height} />
          </div>
        )}
      </Modal>

      {admin && editing && (
        <div className="flex flex-col gap-2 border-b border-admin_bordered pb-3">
          <TermsEditor orgId={orgId} current={pending || inForce} inForce={inForce} onDone={() => (setEditing(false), onChanged?.())} onCancel={() => setEditing(false)} />
        </div>
      )}

      <ContractHistory
        orgId={orgId}
        list={contract?.list || []}
        admin={admin}
        onChanged={onChanged}
        locale={locale}
        // the contract waiting for the vendor is acted on from its own row: signed, or sent again / withdrawn by the admin
        actions={(c) =>
          pending &&
          c._id === pending._id && (
            <>
              {canSign && <SignPanel contract={pending} onSigned={() => onChanged?.()} />}
              {admin && (
                <ActionsMenu
                  testId="contract-row-menu"
                  items={[
                    { label: resending ? "Sending…" : "Send again", disabled: resending, onClick: resend, testId: "contract-resend" },
                    { label: "Withdraw", danger: true, onClick: async () => (await PayoutsAdmin("contract_void", { contractId: pending._id }), onChanged?.()), testId: "contract-withdraw" },
                  ]}
                />
              )}
            </>
          )
        }
        signerNote={!canSign && !admin ? t`An admin of your company signs the contract.` : null}
        // the admin's contract actions, as a menu in the history's heading
        adminActions={
          admin && !editing
            ? [
                { label: inForce ? "Change the contract…" : pending ? "New contract terms…" : "Set terms and send contract…", onClick: () => setEditing(true), testId: "contract-new-terms" },
                // a contract waiting for a signature, of the template's text: filled again with the company's current details, nobody told
                ...(pending && pending.source !== "upload_for_signature" ? [{ label: regenerating ? "Regenerating…" : "Regenerate with current company details", disabled: regenerating, onClick: regenerate, testId: "contract-regenerate" }] : []),
              ]
            : []
        }
      />
    </div>
  )
}
